import { forwardRef, useImperativeHandle, useRef, useState } from "react"
import { useFormContext } from "react-hook-form"

import usePlacesAutocomplete from "@hooks/usePlacesAutocomplete"

import { faIcon } from "@utils/faIcon"

import { EmptyState } from "@molecules"
import Input from "@molecules/Form/inputs/Input/Input"
import { InputName, InputProps } from "@molecules/Form/inputs/Input/Input.types"
import Select from "@molecules/Select/Select"

import styles from "./AddressAutocompleteInput.module.scss"
import { AddressAutocompleteInputProps } from "./AddressAutocompleteInput.types"

const LOADING_SKELETON_COUNT = 3

function AddressAutocompleteInput<T extends InputName = InputName>(
    props: AddressAutocompleteInputProps<T>,
    ref: React.Ref<HTMLInputElement>,
) {
    const {
        countryRestrictions,
        onAddressSelect,
        onManualEntry,
        showManualEntryCTA = true,
        onChange,
        onClear,
        name,
        ...rest
    } = props

    const inputRef = useRef<HTMLInputElement>(null)
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, [])

    const form = useFormContext()

    const {
        suggestions,
        isLoading,
        isError,
        isTyping,
        isQueryReady,
        onSearchChange,
        resetSearch,
        selectSuggestion,
        searchData,
    } = usePlacesAutocomplete({ countryRestrictions })

    const [isFocused, setIsFocused] = useState<boolean>(false)

    const hasNoResults = isQueryReady && !isLoading && !isTyping && !isError && suggestions.length === 0
    const showCTA = Boolean(onManualEntry) && showManualEntryCTA

    const isOpen = isFocused && (suggestions.length > 0 || isLoading || isError || hasNoResults)

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onSearchChange(event.target.value)
        onChange?.(event)
    }

    const handleInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(true)
        rest.onFocus?.(event)
    }

    const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const next = event.relatedTarget as HTMLElement | null
        // If focus is moving to something inside the popover content (e.g. the
        // mousedown on a Select.ListItem), keep the dropdown open so the click
        // can land. The popover content lives in a Radix portal tagged with
        // `data-radix-popper-content-wrapper`.
        if (next?.closest("[data-radix-popper-content-wrapper]")) {
            return
        }
        setIsFocused(false)
        rest.onBlur?.(event)
    }

    const handleClear = () => {
        resetSearch()
        onClear?.()
    }

    const handleOpenChange = () => {
        // No-op. Open state is fully derived from input focus. Reacting to
        // Radix's onOpenChange would clobber focus on trigger clicks (Radix
        // toggles its open state on every trigger click, but we want clicks
        // on the input — which IS the trigger — to keep the popover open).
        // Outside clicks blur the input (handled in handleInputBlur). Escape
        // is handled in handleInputKeyDown.
    }

    const handleSuggestionSelect = async (placeId: string) => {
        const parsed = await selectSuggestion(placeId)
        if (!parsed) {
            return
        }

        // Push to RHF directly. Going through a synthetic input event would re-fire
        // handleInputChange and re-trigger the autocomplete hook for the
        // just-selected address.
        if (form?.setValue) {
            form.setValue(name as unknown as string, parsed.street, {
                shouldValidate: true,
                shouldDirty: true,
            })
        } else if (inputRef.current) {
            // Uncontrolled fallback (isControlled={false} on the underlying Input).
            inputRef.current.value = parsed.street
        }

        // Restore focus so the user can continue editing without re-clicking.
        // Mousedown on the suggestion item shifted focus onto the item div.
        inputRef.current?.focus()

        onAddressSelect?.(parsed)
    }

    const handleManualEntry = () => {
        inputRef.current?.blur()
        onManualEntry?.()
    }

    const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        // Escape: blur the input so the focus-driven close path fires.
        if (event.code === "Escape" && isOpen) {
            event.preventDefault()
            inputRef.current?.blur()
            return
        }

        // When the dropdown is closed, stop arrow/enter from reaching cmdk (which would
        // try to navigate unmounted items or accidentally select something).
        if (!isOpen) {
            const navKeys = ["ArrowUp", "ArrowDown", "Enter", "NumpadEnter"]
            if (navKeys.includes(event.code)) {
                event.stopPropagation()
            }
            return
        }

        // While open, stop Enter from submitting any surrounding form — cmdk handles selection.
        if (event.code === "Enter" || event.code === "NumpadEnter") {
            event.preventDefault()
        }
    }

    return (
        <Select.Root
            mode="popover"
            mobilePresentation="inline"
            isOpen={isOpen}
            onOpenChange={handleOpenChange}
            shouldFilter={false}
            searchMode="external"
            searchData={searchData}
        >
            <Select.Trigger>
                <div className={styles.triggerAnchor}>
                    <Input
                        {...(rest as unknown as InputProps<T>)}
                        name={name}
                        ref={inputRef}
                        type="text"
                        autoComplete="off"
                        onChange={handleInputChange}
                        onClear={handleClear}
                        onKeyDown={handleInputKeyDown}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                    />
                </div>
            </Select.Trigger>

            {isOpen && (
                <Select.Viewport
                    onOpenAutoFocus={(event) => event.preventDefault()}
                    onCloseAutoFocus={(event) => event.preventDefault()}
                >
                    <Select.List title="Address suggestions" className={styles.list} emptyState={null}>
                        <Select.Loading>
                            <Select.ListGroup>
                                {Array.from({ length: LOADING_SKELETON_COUNT }, (_, index) => (
                                    <Select.ListItem
                                        key={`loading-${index}`}
                                        variant="default"
                                        item={{ label: "", value: `loading-${index}` }}
                                        isLoading={true}
                                    />
                                ))}
                            </Select.ListGroup>
                        </Select.Loading>

                        {suggestions.length > 0 && (
                            <Select.ListGroup>
                                {suggestions.map((suggestion) => (
                                    <Select.ListItem
                                        key={suggestion.placeId}
                                        variant="default"
                                        item={{ label: suggestion.fullText, value: suggestion.placeId }}
                                        // Don't surface the lingering Select selection state. The user
                                        // may have edited e.g. manual address fields since picking, so
                                        // the previously-chosen place may no longer match the form.
                                        isSelected={false}
                                        onSelect={(item) => void handleSuggestionSelect(item.value)}
                                    />
                                ))}
                                <div className={styles.attribution}>Powered by Google</div>
                            </Select.ListGroup>
                        )}

                        <Select.NoResults>
                            <EmptyState
                                variant="default"
                                icon={faIcon({ name: "map-location-dot", style: "regular" })}
                                title="No addresses found"
                                supportText={
                                    showCTA
                                        ? "Try a different search or enter the address manually."
                                        : "Try a different search."
                                }
                                primaryCTA={
                                    showCTA
                                        ? {
                                              children: "Enter address manually",
                                              leftIcon: faIcon({ name: "i-cursor", style: "regular" }),
                                              onClick: handleManualEntry,
                                          }
                                        : undefined
                                }
                            />
                        </Select.NoResults>

                        <Select.Error>
                            <EmptyState
                                variant="error"
                                icon={faIcon({ name: "circle-exclamation", style: "solid" })}
                                title="Unable to load addresses"
                                supportText="Check your connection and try again."
                            />
                        </Select.Error>
                    </Select.List>
                </Select.Viewport>
            )}
        </Select.Root>
    )
}

interface AddressAutocompleteInputComponent {
    <T>(props: AddressAutocompleteInputProps<T> & React.RefAttributes<HTMLInputElement>): React.ReactElement | null
    displayName?: string
}

const AddressAutocompleteInputWithRef = forwardRef(
    AddressAutocompleteInput,
) as unknown as AddressAutocompleteInputComponent
AddressAutocompleteInputWithRef.displayName = "AddressAutocompleteInput"

export default AddressAutocompleteInputWithRef
